import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { useStore } from "./store/useStore";
import { RequireAuth } from "./lib/auth";
import { useRuntimeMode } from "./lib/runtime";
import EnvBanner from "./components/EnvBanner";

import Attract from "./pages/Attract";
import LanguagePicker from "./pages/LanguagePicker";
import Menu from "./pages/Menu";
import NameInput from "./pages/NameInput";
import Upsell from "./pages/Upsell";
import OrderReview from "./pages/OrderReview";
import TerminalGuide from "./pages/TerminalGuide";
import ThankYou from "./pages/ThankYou";
import SignInPage from "./pages/SignInPage";

// Each non-kiosk surface is its own lazy chunk so a kiosk / server tablet / KDS
// screen only downloads the code it actually runs — not all of them. (The core
// kiosk attract→pay flow above stays eager: it's small and latency-sensitive.)
const TableLanding = lazy(() => import("./pages/TableLanding"));
const CFD = lazy(() => import("./pages/CFD"));
const KioskJoin = lazy(() => import("./pages/KioskJoin"));
const PublicMenu = lazy(() => import("./pages/PublicMenu"));
const ServerHome = lazy(() => import("./pages/server/ServerHome"));
const ServerTab = lazy(() => import("./pages/server/ServerTab"));
const EightySix = lazy(() => import("./pages/server/EightySix"));
const KDS = lazy(() => import("./pages/kds/KDS"));
const PayAtTable = lazy(() => import("./pages/PayAtTable"));
// Admin is its own chunk: kiosk tablets shouldn't download 35 back-office
// pages, and admin deploys shouldn't bust the kiosk's cached bundle.
const AdminArea = lazy(() => import("./pages/admin/AdminArea"));
// Local-admin console (device runtime) — its own chunk; the cloud bundle never
// downloads it, and the box's admin surface is deliberately a slim subset.
const LocalAdminArea = lazy(() => import("./pages/admin/LocalAdminArea"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior }); }, [pathname]);
  return null;
}

// Root + unknown-route landing. Cloud → Clerk sign-in. Local → straight to the
// server tablet; the device-pairing gate (not Clerk) protects it there. The
// mode probe is async, so hold on "loading" rather than flash the Clerk page on
// the LAN box. (Cloud resolves "cloud" synchronously on *.pages.dev — no flash.)
function RootRedirect() {
  const mode = useRuntimeMode();
  if (mode === "loading")
    return <div className="pos min-h-screen grid place-items-center text-sm text-gray-500">Cargando…</div>;
  return <Navigate to={mode === "local" ? "/server" : "/sign-in"} replace />;
}

// /admin resolves by runtime. Cloud → the full Clerk-gated back office. Local →
// the offline local-admin console: a local_admin-PIN-gated subset touching ONLY
// box-owned data (stock, cost, waste, cash, 86 state, local print queue,
// reports). Catalog editing (menu, prices, staff, permissions, settings) stays
// cloud-only — it's cloud-owned and pulls to the box one-way, so a local edit
// would be silently overwritten on the next catalog pull.
function AdminOrFloor() {
  const mode = useRuntimeMode();
  const fallback = <div className="pos min-h-screen grid place-items-center text-sm text-gray-500">Cargando…</div>;
  if (mode === "loading") return fallback;
  const Area = mode === "local" ? LocalAdminArea : AdminArea;
  return <Suspense fallback={fallback}><Area /></Suspense>;
}

const IDLE_MS = 60_000;

export default function App() {
  const reset = useStore((s) => s.reset);
  const { pathname } = useLocation();
  const isKiosk = pathname === "/kiosk" || pathname.startsWith("/kiosk/");
  // During /kiosk/pay the guest's hands are on the card terminal, not the
  // screen — a 60s no-touch reset would tear down an in-flight payment
  // (charged card, no record). TerminalGuide owns timeout/reset there.
  const isPaying = pathname === "/kiosk/pay";

  useEffect(() => {
    if (!isKiosk || isPaying) return; // only the kiosk surfaces auto-reset
    let t: ReturnType<typeof setTimeout>;
    const bump = () => { clearTimeout(t); t = setTimeout(() => { reset(); window.location.assign("/kiosk"); }, IDLE_MS); };
    ["pointerdown", "keydown"].forEach((e) => window.addEventListener(e, bump));
    bump();
    return () => { clearTimeout(t); ["pointerdown", "keydown"].forEach((e) => window.removeEventListener(e, bump)); };
  }, [reset, isKiosk, isPaying]);

  return (
    <>
      <EnvBanner />
      <ScrollToTop />
      <Suspense fallback={<div className="pos min-h-screen grid place-items-center text-sm text-gray-500">Cargando…</div>}>
      <Routes>
        {/* Root → staff sign-in (cloud) or the server tablet (local). Kiosk
            tablets are pointed at /kiosk directly (homepage/bookmark), not root. */}
        <Route path="/" element={<RootRedirect />} />

        {/* Kiosk (public) */}
        <Route path="/kiosk" element={<Attract />} />
        <Route path="/kiosk/language" element={<LanguagePicker />} />
        <Route path="/kiosk/menu" element={<Menu />} />
        <Route path="/kiosk/name" element={<NameInput />} />
        <Route path="/kiosk/upsell" element={<Upsell />} />
        <Route path="/kiosk/review" element={<OrderReview />} />
        <Route path="/kiosk/pay" element={<TerminalGuide />} />
        <Route path="/kiosk/thanks" element={<ThankYou />} />
        <Route path="/kiosk/join" element={<KioskJoin />} />

        {/* Auth */}
        <Route path="/sign-in/*" element={<SignInPage />} />

        {/* Admin — lazy chunk; see AdminArea.tsx for the route table */}
        <Route path="/admin/*" element={<RequireAuth><AdminOrFloor /></RequireAuth>} />

        {/* Server tablet (Clerk-auth on the device, then PIN per tx) */}
        <Route path="/server" element={<RequireAuth><ServerHome /></RequireAuth>} />
        <Route path="/server/tabs/:id" element={<RequireAuth><ServerTab /></RequireAuth>} />
        <Route path="/server/86" element={<RequireAuth><EightySix /></RequireAuth>} />

        {/* KDS */}
        <Route path="/kds" element={<RequireAuth><KDS /></RequireAuth>} />

        {/* Public read-only menu (share/QR/Instagram-bio link) */}
        <Route path="/menu" element={<PublicMenu />} />
        {/* Public pay-at-table */}
        <Route path="/t/:id" element={<PayAtTable />} />
        {/* Public table QR landing */}
        <Route path="/qr/:tableId" element={<TableLanding />} />
        {/* Customer-facing display (point in front of guest) */}
        <Route path="/cfd" element={<CFD />} />

        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      </Suspense>
    </>
  );
}
